import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Container,
  Chip,
  TextField,
  MenuItem,
  Grid,
  Paper,
  Link,
} from "@mui/material";
import { authenticatedFetch } from "../../utils/api";
import DiaCircle from "../DiaCircle";
import GaugeChip from "../GaugeChip";
import { useTranslation } from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface InventoryItem {
  company_name: string;
  dia: number;
  gauge: string;
  machine_type: string;
  rolls: number;
  weight: number;
}

interface Company {
  id: number;
  name: string;
}

interface Machine {
  id: number;
  dia: number;
  gauge: string;
  machine_type: string;
}

export default function InventoryList() {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [filteredInventory, setFilteredInventory] = useState<InventoryItem[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const { translations: t } = useTranslation();
  
  // Filters
  const [selectedCompany, setSelectedCompany] = useState<string>("all");
  const [selectedMachineType, setSelectedMachineType] = useState<string>("all");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [inventory, selectedCompany, selectedMachineType]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch inventory, companies, and machines in parallel
      const [inventoryResponse, companiesResponse, machinesResponse] = await Promise.all([
        authenticatedFetch(`${API_URL}/inventory`),
        authenticatedFetch(`${API_URL}/companies?per_page=1000&minimal=true`),
        authenticatedFetch(`${API_URL}/machines`),
      ]);

      if (!inventoryResponse.ok) {
        throw new Error("Failed to fetch inventory");
      }
      if (!companiesResponse.ok) {
        throw new Error("Failed to fetch companies");
      }
      if (!machinesResponse.ok) {
        throw new Error("Failed to fetch machines");
      }

      const inventoryData = await inventoryResponse.json();
      const companiesData = await companiesResponse.json();
      const machinesData = await machinesResponse.json();

      setInventory(inventoryData);
      setCompanies(companiesData);
      setMachines(machinesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...inventory];

    // Filter by company
    if (selectedCompany !== "all") {
      filtered = filtered.filter((item) => item.company_name === selectedCompany);
    }

    // Filter by machine type
    if (selectedMachineType !== "all") {
      filtered = filtered.filter((item) => item.machine_type === selectedMachineType);
    }

    setFilteredInventory(filtered);
  };

  const getTotalRolls = () => {
    return Math.floor(filteredInventory.reduce((sum, item) => sum + (Number(item.rolls) || 0), 0));
  };

  const getTotalWeight = () => {
    const total = filteredInventory.reduce((sum, item) => sum + (Number(item.weight) || 0), 0);
    return total.toFixed(2);
  };

  const getUniqueCompanies = () => {
    const uniqueNames = Array.from(new Set(inventory.map((item) => item.company_name)));
    return uniqueNames.sort();
  };

  const getUniqueMachineTypes = () => {
    const uniqueTypes = Array.from(new Set(inventory.map((item) => item.machine_type)));
    return uniqueTypes.sort();
  };

  const getCompanyIdByName = (companyName: string) => {
    const company = companies.find((c) => c.name === companyName);
    return company?.id;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Card sx={{ mt: { xs: 2, sm: 4 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Summary Cards */}
          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  textAlign: "center",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Total Items
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {filteredInventory.length}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  textAlign: "center",
                  background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  color: "white",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Total Rolls
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {getTotalRolls()}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  textAlign: "center",
                  background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                  color: "white",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Total Weight
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {getTotalWeight()} kg
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  textAlign: "center",
                  background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                  color: "white",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Companies
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {getUniqueCompanies().length}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Filters */}
          <Box sx={{ mb: { xs: 2, sm: 3 }, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              select
              label="Company"
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 200 } }}
              size="small"
            >
              <MenuItem value="all">All Companies</MenuItem>
              {getUniqueCompanies().map((company) => (
                <MenuItem key={company} value={company}>
                  {company}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Machine Type"
              value={selectedMachineType}
              onChange={(e) => setSelectedMachineType(e.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 200 } }}
              size="small"
            >
              <MenuItem value="all">All Types</MenuItem>
              {getUniqueMachineTypes().map((type) => (
                <MenuItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Inventory Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Dia</TableCell>
                  <TableCell>Gauge</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Machine Type</TableCell>
                  <TableCell align="right">Rolls</TableCell>
                  <TableCell align="right">Weight (kg)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInventory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {t.inventory.noInventory}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInventory.map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                      }}
                    >
                      <TableCell>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={(e) => {
                            e.stopPropagation();
                            const companyId = getCompanyIdByName(item.company_name);
                            if (companyId) {
                              navigate(`/companies/${companyId}`);
                            }
                          }}
                          sx={{ 
                            fontWeight: 500,
                            textDecoration: 'none', 
                            '&:hover': { textDecoration: 'underline' },
                            display: 'block',
                            textAlign: 'left',
                          }}
                        >
                          {item.company_name}
                        </Link>
                        <Box sx={{ display: { sm: 'none' }, mt: 0.5, alignItems: 'center', gap: 1 }}>
                          <DiaCircle
                            dia={item.dia}
                            gauge={item.gauge}
                            machineType={item.machine_type}
                            machines={machines}
                            size="small"
                          />
                          <Chip
                            label={item.machine_type}
                            size="small"
                            sx={{
                              ml: 1,
                              bgcolor: item.machine_type === 'rib' ? '#e0e0e0' : '#757575',
                              color: item.machine_type === 'rib' ? '#000' : '#fff',
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <DiaCircle
                          dia={item.dia}
                          gauge={item.gauge}
                          machineType={item.machine_type}
                          machines={machines}
                          size="medium"
                        />
                      </TableCell>
                      <TableCell>
                        <GaugeChip gauge={item.gauge} />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Chip
                          label={item.machine_type}
                          size="small"
                          sx={{
                            bgcolor: item.machine_type === 'rib' ? '#e0e0e0' : '#757575',
                            color: item.machine_type === 'rib' ? '#000' : '#fff',
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600">
                          {Number(item.rolls || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600">
                          {Number(item.weight || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Container>
  );
}

